import { useEffect } from 'react';
import { Route, Routes, useLocation, useNavigationType } from 'react-router-dom';
import { ErrorBoundary } from '@/components/layout/ErrorBoundary';
import { SiteFooter } from '@/components/layout/SiteFooter';
import { SiteHeader } from '@/components/layout/SiteHeader';
import { captureSource } from '@/lib/source';
import { track } from '@/lib/analytics';
import { ContactPage } from '@/pages/ContactPage';
import { HomePage } from '@/pages/HomePage';
import { JourneyPage } from '@/pages/JourneyPage';
import { MoreAboutPage } from '@/pages/MoreAboutPage';
import { NotFoundPage } from '@/pages/NotFoundPage';
import { PrivacyPage } from '@/pages/PrivacyPage';
import { ProfilePage } from '@/pages/ProfilePage';
import { ResultPage } from '@/pages/ResultPage';

/**
 * Starts each new route at the top of the page and moves focus to the main
 * landmark, so keyboard and screen-reader users are not silently left on a page
 * they cannot tell has changed. React Router does neither of these for you.
 */
function RouteAnnouncer() {
  const { pathname, search } = useLocation();
  const navigationType = useNavigationType();

  useEffect(() => {
    const source = captureSource(search);
    track({ name: 'page_opened', page: pathname, source: source ?? undefined });

    /*
     * A new page begins at its beginning. Focus alone does not do this: #main
     * is focused with `preventScroll`, so without an explicit jump the browser
     * keeps whatever offset the previous page was at — following "How I try to
     * show up" from the foot of the values section landed you two thirds of
     * the way down the page you had just opened.
     *
     * POP is left alone. That is Back and Forward, where restoring the position
     * someone left from is the right answer, not a bug.
     *
     * 'instant' is deliberate: `html { scroll-behavior: smooth }` would
     * otherwise animate the full height of a long page on every navigation.
     */
    if (navigationType !== 'POP') {
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    }

    const main = document.getElementById('main');
    if (main) {
      main.focus({ preventScroll: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  return null;
}

export default function App() {
  return (
    <>
      <a className="skip-link" href="#main">
        Skip to content
      </a>
      <RouteAnnouncer />
      <SiteHeader />
      <main id="main" tabIndex={-1}>
        <ErrorBoundary>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/404" element={<NotFoundPage />} />
            <Route path="/:slug" element={<ProfilePage />} />
            <Route path="/:slug/how-i-show-up" element={<MoreAboutPage />} />
            <Route path="/:slug/questions" element={<JourneyPage />} />
            <Route path="/:slug/result" element={<ResultPage />} />
            {/* The other way in: say hello without doing the questions. */}
            <Route path="/:slug/contact" element={<ContactPage />} />
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </ErrorBoundary>
      </main>
      <SiteFooter />
    </>
  );
}
